import {ENDPOINTS} from '../common/endpoints';
import {SignedDownload} from './SignedImageUpload';

const STORAGE_KEY = `mybake:signed-downloads:v1:${ENDPOINTS.BASE_API_URL}`;
const MAX_ENTRIES = 200;
const isValid = (value: unknown): value is SignedDownload => {
    if (!value || typeof value !== 'object') return false;
    const item = value as SignedDownload;
    return typeof item.url === 'string' && /^https?:\/\//.test(item.url)
        && typeof item.expiresAt === 'string' && Date.parse(item.expiresAt) > Date.now() + 10000;
};

// Read storage on each lookup so other tabs' updates and logout are respected.
export const createSignedDownloadCache = () => {
    let fallback: Record<string, SignedDownload> = {};
    let generation = 0;
    let memoryOnly = false;
    const pending = new Map<string, Promise<SignedDownload>>();
    const read = (): Record<string, SignedDownload> => {
        if (memoryOnly) return fallback;
        try {
            const parsed: unknown = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '{}');
            return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed as Record<string, SignedDownload> : {};
        } catch { return fallback; }
    };
    const write = (entries: Record<string, SignedDownload>) => {
        fallback = Object.fromEntries(Object.entries(entries).filter(([, value]) => isValid(value))
            .sort(([, a], [, b]) => Date.parse(b.expiresAt) - Date.parse(a.expiresAt)).slice(0, MAX_ENTRIES));
        try { localStorage.setItem(STORAGE_KEY, JSON.stringify(fallback)); } catch { memoryOnly = true; }
    };
    return {
        get(key: string, load: () => Promise<SignedDownload>): Promise<SignedDownload> {
            const entries = read();
            if (isValid(entries[key])) return Promise.resolve(entries[key]);
            if (pending.has(key)) return pending.get(key)!;
            const started = generation;
            const request = Promise.resolve().then(load).then(download => {
                if (started === generation && isValid(download)) write({...read(), [key]: download});
                return download;
            }).finally(() => { if (pending.get(key) === request) pending.delete(key); });
            pending.set(key, request);
            return request;
        },
        clear() {
            generation += 1;
            fallback = {};
            memoryOnly = false;
            pending.clear();
            try { localStorage.removeItem(STORAGE_KEY); } catch { /* Storage may be disabled. */ }
        },
    };
};

export const signedDownloadCache = createSignedDownloadCache();
